import React from "react";
import { useLanguage } from "context/LanguageContext";
import ActionButton from "components/ui/ActionButton";
import {
  MdClose,
  MdFileDownload,
  MdOutlineEventAvailable,
} from "react-icons/md";
import { classMgmtService } from "services/admin/classMgmtService";
import { participantInsightsService } from "services/admin/participantInsightsService";
import {
  buildAttendanceHistoryWorkbook,
  downloadWorkbook,
  generateHistoryFilename,
  type ClassSessionWithMeta,
} from "services/admin/excelExportService";
import type {
  ClassListItem,
  SessionParticipant,
  UserListItem,
} from "types/domain";

interface ExportHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedMonth: string;
  students: UserListItem[];
}

const getMonthRange = (month: string) => {
  const [year, m] = month.split("-").map(Number);
  const from = `${month}-01`;
  const lastDay = new Date(year, m, 0).getDate();
  const to = `${month}-${String(lastDay).padStart(2, "0")}`;
  return { from, to };
};

const ExportHistoryModal: React.FC<ExportHistoryModalProps> = ({
  isOpen,
  onClose,
  selectedMonth,
  students,
}) => {
  const { t } = useLanguage();
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [exportMonth, setExportMonth] = React.useState(selectedMonth);
  const [running, setRunning] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [userFilter, setUserFilter] = React.useState("");

  React.useEffect(() => {
    if (isOpen) {
      setSelectedIds(students.map((s) => s.id));
      setExportMonth(selectedMonth);
      setError(null);
      setUserFilter("");
    }
  }, [isOpen, selectedMonth, students]);

  const filteredStudents = React.useMemo(() => {
    if (!userFilter.trim()) return students;
    const q = userFilter.toLowerCase();
    return students.filter(
      (s) =>
        (s.full_name ?? "").toLowerCase().includes(q) ||
        (s.email ?? "").toLowerCase().includes(q) ||
        (s.code ?? "").toLowerCase().includes(q)
    );
  }, [students, userFilter]);

  const toggleStudent = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  if (!isOpen) return null;

  const handleExport = async () => {
    if (!exportMonth) {
      setError("userSessions.export.monthRequired");
      return;
    }
    if (selectedIds.length === 0) {
      setError("userSessions.export.studentRequired");
      return;
    }

    setRunning(true);
    setError(null);
    const monthRange = getMonthRange(exportMonth);
    try {
      // 1. Load all classes
      let classes: ClassListItem[] = [];
      let skip = 0;
      const limit = 200;
      while (true) {
        const res = await classMgmtService.getAll({ skip, limit });
        classes = [...classes, ...res.items];
        if (res.items.length < limit) break;
        skip += limit;
      }

      // 2. Load all sessions in range
      const sessionsFeed: ClassSessionWithMeta[] = [];
      skip = 0;
      while (true) {
        const res = await classMgmtService.getSessionsFeed({
          date_from: `${monthRange.from}T00:00:00`,
          date_to: `${monthRange.to}T23:59:59`,
          skip,
          limit: 200,
        });
        res.items.forEach((s) => {
          sessionsFeed.push({
            ...s,
            class_name: (s as { class_name?: string | null }).class_name ?? "",
            class_code: null,
          });
        });
        if (res.items.length < 200) break;
        skip += limit;
      }

      // 3. Filter sessions to ones that belong to our classes
      const classIds = new Set(classes.map((c) => c.id));
      const classSessions = sessionsFeed.filter(
        (s) => s.class_id && classIds.has(s.class_id)
      );

      // 4. Reuse aggregated feed payload instead of fetching participants per session
      const participantsBySession: Record<string, SessionParticipant[]> =
        Object.fromEntries(
          classSessions.map((session) => [
            session.id,
            (
              session as ClassSessionWithMeta & {
                participants?: SessionParticipant[];
              }
            ).participants ?? [],
          ])
        );

      // 5. Filter sessions to those with at least one student attended
      const relevantSessions = classSessions.filter((s) => {
        const parts = participantsBySession[s.id] ?? [];
        return parts.some(
          (p) =>
            p.role === "student" &&
            (p.status === "checked_in" || p.status === "checked_out")
        );
      });

      // 6. Build per-class student map
      const studentsByClassId: Record<string, UserListItem[]> = {};
      const studentIdSet = new Set<string>();
      relevantSessions.forEach((s) => {
        if (!s.class_id) return;
        const list = studentsByClassId[s.class_id] ?? [];
        (participantsBySession[s.id] ?? []).forEach((p) => {
          if (p.role === "student" && !studentIdSet.has(p.user_id)) {
            studentIdSet.add(p.user_id);
            const match = students.find((u) => u.id === p.user_id);
            if (match) list.push(match);
          }
        });
        studentsByClassId[s.class_id] = list;
      });

      // 7. Final students list from selected IDs
      const finalStudents = students.filter((s) => selectedIds.includes(s.id));

      // 8. Build workbook
      const wb = buildAttendanceHistoryWorkbook({
        fromDate: monthRange.from,
        toDate: monthRange.to,
        classes,
        sessions: relevantSessions,
        participantsBySession,
        students: finalStudents,
        studentsByClassId,
        includeAllEnrolled: false,
        month: exportMonth,
        t,
      });

      downloadWorkbook(wb, generateHistoryFilename(exportMonth));
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "userSessions.export.failed"
      );
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="bg-black/50 fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl rounded-2xl bg-surface shadow-2xl">
        <div className="flex items-center justify-between border-b border-line px-6 py-4">
          <div className="flex items-center gap-2">
            <MdOutlineEventAvailable className="h-5 w-5 text-brand-500" />
            <h3 className="text-lg font-semibold text-ink">
              {t("exportHistory.title")}
            </h3>
          </div>
          <ActionButton
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="!p-1.5 text-ink-subtle hover:text-gray-600"
            aria-label={t("action.close")}
          >
            <MdClose className="h-5 w-5" />
          </ActionButton>
        </div>

        <div className="space-y-5 px-6 py-5">
          {/* Month filter */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-ink-muted">
                {t("userSessions.export.month")}
              </label>
              <input
                type="month"
                value={exportMonth}
                onChange={(e) => setExportMonth(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-surface-muted dark:text-white"
              />
            </div>
          </div>

          {/* Student picker */}
          <div>
            <label className="mb-2 block text-sm font-medium text-ink-muted">
              {t("userSessions.export.selectedCount", {
                count: selectedIds.length,
              })}
            </label>
            <input
              type="text"
              placeholder={t("exportHistory.searchPlaceholder")}
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              className="mb-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-surface-muted dark:text-white"
            />
            <div className="max-h-56 overflow-auto rounded-lg border border-line">
              {filteredStudents.length === 0 ? (
                <p className="px-3 py-2 text-sm text-ink-muted">
                  {t("userSessions.export.noStudents")}
                </p>
              ) : (
                filteredStudents.slice(0, 200).map((s) => (
                  <label
                    key={s.id}
                    className="flex cursor-pointer items-center gap-2 border-b border-line px-3 py-2 text-sm last:border-b-0 hover:bg-navy-50 dark:hover:bg-white/5"
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(s.id)}
                      onChange={() => toggleStudent(s.id)}
                      className="accent-brand-500"
                    />
                    <span className="font-medium text-ink">
                      {s.full_name ??
                        s.email ??
                        s.code ??
                        t("enum.role.STUDENT")}
                    </span>
                    {s.email ? (
                      <span className="text-ink-muted">({s.email})</span>
                    ) : null}
                  </label>
                ))
              )}
              {filteredStudents.length > 200 && (
                <p className="px-3 py-2 text-xs text-ink-subtle">
                  {t("userSessions.export.showingLimited", {
                    shown: 200,
                    total: filteredStudents.length,
                  })}
                </p>
              )}
            </div>
          </div>

          {error && <p className="text-sm text-red-500">{t(error)}</p>}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-line px-6 py-4">
          <ActionButton variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </ActionButton>
          <ActionButton
            onClick={handleExport}
            disabled={running}
            startIcon={<MdFileDownload className="h-4 w-4" />}
          >
            {running ? t("exportHistory.exporting") : t("exportHistory.submit")}
          </ActionButton>
        </div>
      </div>
    </div>
  );
};

export default ExportHistoryModal;
